import * as Sentry from '@sentry/react';
import { Domain } from '@superfunnel/types';
import AlertModal from '@superfunnel/ui-core/AlertModal';
import Loader from '@superfunnel/ui-core/Loader';
import { Separator } from '@superfunnel/ui-core/v2/Separator';
import { classNames } from '@superfunnel/utils';
import { CheckCircle, OpenNewWindow, Trash, XmarkCircle } from 'iconoir-react';
import { RefreshCw } from 'lucide-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { SnackbarContext } from '../../contexts/SnackbarContext';
import { UserContext } from '../../contexts/UserContext';
import {
  deleteDomain as deleteDomainApi,
  refreshDomainStatus,
} from '../../services/DomainsService';
import ConfiguredSection from './ConfiguredSection';

interface Props {
  domainId: string;
  domain: Domain;
}

const DomainCard = ({ domainId, domain }: Props) => {
  const { show } = useContext(SnackbarContext);
  const { selectedAccount } = useContext(UserContext);

  const [isValidating, setIsValidating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const deleteDomain = useCallback(async () => {
    setRemoving(true);
    try {
      await deleteDomainApi(selectedAccount?.id ?? '', domainId);
      show('Domain deleted');
    } catch (e: any) {
      Sentry.captureException(e);
      show('Unable to delete domain ' + e.message);
    } finally {
      setRemoving(false);
      setDeleteConfirmOpen(false);
    }
  }, [selectedAccount?.id, domainId, show]);

  const getDomainStatus = useCallback(() => {
    if (!selectedAccount) return;
    setIsValidating(true);

    refreshDomainStatus(selectedAccount.id, domainId).finally(() => {
      setIsValidating(false);
    });
  }, [selectedAccount, domainId]);

  useEffect(() => {
    if (!selectedAccount) return;
    if (domain.type !== 'custom' || domain.status !== 'pending') return;
    getDomainStatus();
  }, [domain.status, domain.type, getDomainStatus, selectedAccount]);

  return (
    <>
      <AlertModal
        type={'warn'}
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={`Are you sure you want to delete "${domain.domain}"?`}
        description="This will remove the domain from your account. This action cannot be undone."
        actions={[
          {
            title: 'Cancel',
            type: 'secondary',
            disabled: removing,
            onClick: () => setDeleteConfirmOpen(false),
          },
          {
            title: 'Delete',
            type: 'primary',
            color: 'negative',
            loading: removing,
            onClick: deleteDomain,
          },
        ]}
      />
      <div
        className={'rounded-2lg border-base-200 w-full border p-4 shadow-xs'}
      >
        <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3">
          <div
            className={classNames(
              'flex size-9.5 shrink-0 items-center justify-center rounded-full',
              domain.status === 'pending'
                ? 'bg-red-50'
                : domain.status === 'approved'
                  ? 'bg-success-50'
                  : 'bg-red-50',
            )}
          >
            {domain.status === 'approved' ? (
              <CheckCircle className={'text-success-600 size-4.5'} />
            ) : (
              <XmarkCircle className={'size-4.5 text-red-600'} />
            )}
          </div>
          <div className={'font-inter space-y-0.5'}>
            <a
              href={`http://${domain.domain}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 text-left text-sm font-medium"
            >
              {domain.domain}
              <OpenNewWindow className={'text-base-500 size-4'} />
            </a>
            <p
              className={`${
                domain.status === 'approved'
                  ? 'text-base-500 font-normal'
                  : 'font-medium text-red-700'
              } text-xs`}
            >
              {domain.status === 'approved' ? 'Valid' : 'Invalid'} Configuration
            </p>
          </div>
          <div className={'flex items-center gap-1.5'}>
            {domain.type === 'custom' && (
              <>
                <button
                  onClick={() => {
                    getDomainStatus();
                  }}
                  disabled={isValidating}
                  className={`text-base-500 p-1.5 ${isValidating ? 'cursor-not-allowed' : ''}`}
                >
                  {isValidating ? (
                    <Loader />
                  ) : (
                    <RefreshCw className={'size-4.5'} />
                  )}
                </button>
                <Separator orientation={'vertical'} className="h-6" />
                <button
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={removing}
                  className={`text-base-500 p-1.5 ${isValidating ? 'cursor-not-allowed' : ''}`}
                >
                  {removing ? <Loader /> : <Trash className={'size-4.5'} />}
                </button>
              </>
            )}
          </div>
        </div>
        <ConfiguredSection domain={domain} />
      </div>
    </>
  );
};

export default DomainCard;
